'use client';

import { createContext, useCallback, useContext, useMemo } from 'react';

import { PageLoader } from '@crehana/compass.ui/v2';

import { useXTalentAuthorizationToken } from '@/shared/api/x-talent-authorization-token';
import { GlobalInternalErrorBoundary } from '@/shared/components/error-boundaries';
import { useTalentToken } from '@/shared/hooks/use-talent-token';

import { useCentralizedMe } from '@crehana/auth';
import { useGetModuleNames } from './hooks/use-get-module-name';
import { type TTalentUser, useGetUserData } from './hooks/use-get-user-data';

/* context state */
type TContextState = {
  user: TTalentUser;
  getModuleName: (label: string) => string | undefined;
  getModuleId: (label: string) => string | undefined;
  xTalentAuthorizationToken: string;
  talentToken?: string;
  refetchModuleNames: () => void;
};

const Context = createContext<TContextState | undefined>(undefined);

type TTalentAuthContextClientProviderProps = {
  children: React.ReactNode;
};

/**
 * Use the `useTalentAuth` to get the data for the current user.
 * ````tsx
 * import { useTalentAuth } from '@/shared/contexts/talent-auth';
 *
 * const MyComponent = () => {
 *  const { user } = useTalentAuth()
 *  ...
 * }
 * ````
 */
export const TalentAuthContextClientProvider: React.FC<
  TTalentAuthContextClientProviderProps
> = ({ children }) => {
  /* get current session data: talent, learning and centralized */
  const { me: currentSession } = useCentralizedMe();

  /* Memoize variables for useGetUserData to avoid object recreation */
  const userDataVariables = useMemo(
    () =>
      currentSession
        ? {
            companyId: Number(
              currentSession?.security.talentSession?.companyId,
            ),
            userId: Number(currentSession?.security.talentSession?.userId),
            employeeId: Number(
              currentSession?.security.talentSession?.employeeId,
            ),
            userRole: currentSession?.security.talentSession?.userRole,
            userParentId: Number(0),
          }
        : undefined,
    [
      currentSession,
      currentSession?.security.talentSession?.companyId,
      currentSession?.security.talentSession?.userId,
      currentSession?.security.talentSession?.employeeId,
      currentSession?.security.talentSession?.userRole,
    ],
  );

  /* obtains talent user data */
  const {
    user,
    loading: loadingUserData,
    error: errorToGetUserData,
    refetch: refetchUserData,
  } = useGetUserData({
    skip: !currentSession,
    variables: userDataVariables,
  });

  /* Memoize options for useGetModuleNames */
  const moduleNamesOptions = useMemo(
    () =>
      currentSession
        ? {
            skip: false as const,
            companyId: Number(
              currentSession?.security.talentSession?.companyId,
            ),
            user,
          }
        : {
            skip: true as const,
          },
    [currentSession, currentSession?.security.talentSession?.companyId, user],
  );

  /* obtain talent module names translation */
  const {
    loading: loadingModulesName,
    moduleNames,
    error: errorToGetModuleNames,
    refetch: refetchModuleNames,
  } = useGetModuleNames(moduleNamesOptions);

  const handleRefetchModuleNames = useCallback(async () => {
    await refetchModuleNames();
  }, [refetchModuleNames]);

  /* return module name by label - memoized with useCallback */
  const getModuleName = useCallback(
    (label: string) => {
      return moduleNames.find(module => {
        return module.label === label;
      })?.value;
    },
    [moduleNames],
  );

  /* return module id by label - memoized with useCallback */
  const getModuleId = useCallback(
    (label: string) => {
      return (
        moduleNames
          .find(module => {
            return module.label === label;
          })
          ?.id?.toString() ?? undefined
      );
    },
    [moduleNames],
  );

  /* generate talent authorization token x-talent-authorization - only when user is present */
  const {
    token: xTalentAuthorizationToken,
    isPending: isPendingXTalentAuthorizationToken,
  } = useXTalentAuthorizationToken({ user: user || null });

  /* generate talent-token for REST/GraphQL bridge */
  const {
    token: talentToken,
    isPending: isPendingTalentToken,
    isError: isErrorTalentToken,
    error: errorTalentToken,
    refetch: refetchTalentToken,
  } = useTalentToken({
    role: userDataVariables?.userRole,
    employeeId: userDataVariables?.employeeId,
    companyId: userDataVariables?.companyId,
    userId: userDataVariables?.userId,
  });

  /**
   * CORE-1776: el botón "try loading the page again" del error boundary global era un
   * no-op, así que la pantalla de error quedaba permanente y el usuario tenía que
   * refrescar o re-loguearse. El boundary se dispara por cualquiera de las tres fuentes
   * (user data, module names, talent token), así que el reset las reintenta a las tres.
   *
   * Usamos `allSettled` a propósito: si un reintento vuelve a fallar, el hook
   * correspondiente vuelve a exponer su error y el boundary se re-renderiza solo. No
   * queremos que el rechazo de una fuente cancele el reintento de las otras dos ni que
   * escape como unhandled rejection.
   */
  const handleGlobalErrorReset = () => {
    void Promise.allSettled([
      refetchUserData(),
      refetchModuleNames(),
      refetchTalentToken(),
    ]);
  };

  /* Memoize context value to prevent unnecessary re-renders */
  const contextValue = useMemo(
    () => ({
      user: user!, // At this point user cannot be null due to previous validations
      getModuleName,
      getModuleId,
      xTalentAuthorizationToken: xTalentAuthorizationToken || '',
      talentToken,
      refetchModuleNames: handleRefetchModuleNames,
    }),
    [
      user,
      getModuleName,
      getModuleId,
      xTalentAuthorizationToken,
      talentToken,
      handleRefetchModuleNames,
    ],
  );

  /* loading state */
  if (
    loadingUserData ||
    loadingModulesName ||
    isPendingXTalentAuthorizationToken ||
    isPendingTalentToken
  ) {
    return <PageLoader />;
  }

  /* error state */
  if (
    errorToGetUserData ||
    errorToGetModuleNames ||
    isErrorTalentToken ||
    !user
  ) {
    const error =
      errorToGetUserData || errorToGetModuleNames || errorTalentToken;
    console.error('error to get user data', error);

    return (
      <GlobalInternalErrorBoundary
        error={{
          name: error?.name as string,
          message: error?.message as string,
        }}
        reset={handleGlobalErrorReset}
      />
    );
  }

  /* Listener to send the session token to the angular modules iframes */
  window.addEventListener('message', event => {
    if (event.data.event == 'getSession') {
      // Send session token to all angular iframes
      const iframeIds = ['angular-iframe'];

      iframeIds.forEach(iframeId => {
        const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
        if (iframe) {
          iframe.contentWindow?.postMessage(
            {
              event: 'sessionId',
              // TODO: replace with the correct token
              value: xTalentAuthorizationToken,
            },
            '*',
          );
        }
      });
    }
  });

  /* context */
  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useTalentAuth = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error(
      'useTalentAuth must be used within the TalentAuthContextClientProvider.',
    );
  }
  return context;
};
